import React from 'react';
import { useField, useFormikContext } from 'formik';
import { Box, IconButton, Tooltip, Popover, PopoverContent, PopoverTrigger } from 'pounceV2';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import { useTheme } from '@mui/material';
import AIPromptSettingsV3, {
  mapOutputLengthToInitial,
  mapOutputLengthToLabel,
} from './AIPromptSettingsV3';
import { RerunFormValues } from './GenericAISummaryContextV3';
import AIStopButtonV3 from './AIStopButtonV3';
import AIAttachmentPillsV3 from './AIAttachmentPillsV3';
import { useDragAndDrop } from './hooks/useDragAndDrop';
import type { Attachment } from './hooks/useAIAttachments';
import { ATTACHMENT_ACCEPT } from './utils/attachmentConstants';

export type AISummaryPromptProps = {
  alias?: string;
  isLoading: boolean;
  cancelPrompt: () => void;
  disabled?: boolean;
  onAttachFile?: (file: File) => void;
  isAttachDisabled?: boolean;
  isUploading?: boolean;
  attachments?: Attachment[];
  onRemoveAttachment?: (id: string) => void;
};

const AISummaryPromptInputV3 = (props: TextFieldProps & AISummaryPromptProps) => {
  const {
    palette: { tokens, primary, componentTokens },
  } = useTheme();
  const { values, submitCount, submitForm } = useFormikContext<RerunFormValues>();
  const [, meta] = useField(props.name as string);
  const {
    isLoading,
    cancelPrompt,
    disabled,
    onAttachFile,
    isAttachDisabled,
    isUploading,
    attachments,
    onRemoveAttachment,
    ...textProps
  } = props;
  const isInvalid = submitCount > 0 && meta.touched && !!meta.error;
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const canDrop = !!onAttachFile && !isAttachDisabled && !disabled && !isLoading;
  const { isDragOver, handleDragEnter, handleDragOver, handleDragLeave, handleDrop } =
    useDragAndDrop({
      canDrop,
      onDrop: onAttachFile,
    });

  const showAttachmentArea = !!attachments && !!onRemoveAttachment;
  const hasAttachments = showAttachmentArea && attachments.length > 0;

  return (
    <Box
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      borderRadius={1}
      border="1px solid"
      borderColor={isDragOver ? primary.main : tokens.stroke.neutral[4]}
      backgroundColor={componentTokens.inputs.colors.normal.background.resting}
      overflow="hidden"
    >
      {showAttachmentArea && (
        <Box
          display="grid"
          gridTemplateRows={hasAttachments ? '1fr' : '0fr'}
          transition="grid-template-rows 150ms ease"
        >
          <Box overflow="hidden" minHeight={0}>
            <Box px={2} pt={1.5}>
              <AIAttachmentPillsV3 attachments={attachments} onRemove={onRemoveAttachment} />
            </Box>
          </Box>
        </Box>
      )}
      <TextField
        multiline
        maxRows={10}
        minRows={1}
        error={isInvalid}
        helperText={isInvalid ? meta.error : ''}
        fullWidth
        onKeyDown={e => {
          if (e.key === 'Enter' && !e.shiftKey && !isUploading) {
            e.preventDefault();
            submitForm();
          }
        }}
        size="small"
        disabled={isLoading || disabled}
        sx={{
          backgroundColor: 'transparent',
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'transparent',
            '&:not(.Mui-disabled):hover': { backgroundColor: 'transparent' },
            '&.Mui-focused': { backgroundColor: 'transparent' },
          },
        }}
        InputProps={{
          endAdornment: (
            <>
              {onAttachFile && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    multiple
                    accept={ATTACHMENT_ACCEPT}
                    onChange={e => {
                      Array.from(e.target.files || []).forEach(f => onAttachFile(f));
                      // Reset so the same file can be re-selected
                      e.target.value = '';
                    }}
                  />
                  <Tooltip content="Attach file" placement="top">
                    <Box as="span" display="inline-flex">
                      <IconButton
                        icon="place-file"
                        aria-label="Attach file"
                        size="medium"
                        variant="ghost"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={disabled || isAttachDisabled}
                      />
                    </Box>
                  </Tooltip>
                </>
              )}
              <Popover>
                {/*
                popover trigger creates a button which conflicts with the tooltip button. we turn off all styling
                for said button and instead rely on the styling of MuiTooltip
              */}
                <Box position="relative">
                  <Tooltip
                    content={`Reasoning level: ${mapOutputLengthToLabel(values.outputLength)}`}
                    placement="top"
                  >
                    <Box as="span" display="inline-flex">
                      <PopoverTrigger
                        as={IconButton}
                        icon="settings-line"
                        aria-label="edit prompt settings"
                        size="medium"
                        variant="ghost"
                        disabled={disabled}
                      />
                    </Box>
                  </Tooltip>
                  <Box
                    position="absolute"
                    top={0}
                    right={0}
                    backgroundColor={tokens.background.brand.blue[4]}
                    px="4px"
                    fontSize="x-small"
                    fontWeight="bold"
                    color={tokens.content.text.neutral.base}
                    style={{
                      transform: 'translate(25%, -25%)',
                      minWidth: '14px',
                      height: '14px',
                      borderRadius: '50%',
                      lineHeight: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {mapOutputLengthToInitial(values.outputLength)}
                  </Box>
                </Box>
                <PopoverContent alignment="bottom-center" zIndex={1400}>
                  <AIPromptSettingsV3 outputLength={values.outputLength} />
                </PopoverContent>
              </Popover>
              {isLoading ? (
                <AIStopButtonV3 cancelPrompt={cancelPrompt} sx={{ mr: 2 }} />
              ) : (
                <Tooltip content="Submit" placement="top">
                  <Box as="span">
                    <IconButton
                      aria-label="Submit"
                      icon="paper-plane"
                      size="medium"
                      type="button"
                      variant="ghost"
                      flexShrink={0}
                      mr={2}
                      onClick={() => submitForm()}
                      disabled={disabled || isUploading}
                    />
                  </Box>
                </Tooltip>
              )}
            </>
          ),
          // Prevent 1Password from suggesting a password
          inputProps: {
            autoComplete: 'new-password',
            id: 'search-ai-summary-prompt-input',
          },
          sx: {
            textarea: {
              padding: 2,
              margin: 1,
              resize: 'none',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
          },
        }}
        {...textProps}
      />
    </Box>
  );
};

export default AISummaryPromptInputV3;
